/**
 * BOOMBA US - Lock the quantity on Recharge Quantity Upsell lines in the AMP cart.
 *
 * A Quantity Upsell plan bakes the number of units into the plan itself, so the cart
 * line quantity is not the customer's to change. Letting them tap the stepper would put
 * the line out of step with the subscription Recharge creates.
 *
 * Recharge's own cart does this for you. We run AMP's cart instead, and their doc says:
 * "If you're not using the Recharge Cart, you'll need to ensure that quantities can't be
 * changed on Quantity upsell products."
 *
 * This is display only. The real guard is the Shopify checkout rule
 * "Recharge Cart Validation for Quantity Upsells", which blocks a tampered cart.
 *
 * Own asset on purpose: amp-upsell.js stays byte-identical and rollback is deleting this
 * file plus one loader line. Must be loaded AFTER amp-upsell.js in theme.liquid, both
 * deferred, so this file's AMP_CART_UPDATED assignment wins.
 */
(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Config
  // ---------------------------------------------------------------------------

  const SELECTORS = {
    // AMP slide cart
    cartLine: ".amp-cart__cart-line",
    quantitySelector: ".amp-cart__line-quantity-selector",
    detailsBottom: ".amp-cart__cart-line-details-bottom",
    ampRoot: "#amp-theme-extension",

    // The theme's own cart surfaces: /cart and the native drawer share this markup.
    themeQuantityInput: 'input[name="updates[]"][data-index]',
    themeQuantityWrap: "hdt-quantity-wrapp",
  };

  // Hiding the stepper outright would also hide HOW MANY the customer is getting, which
  // is the whole point of the offer. Show it as static text instead.
  const SHOW_STATIC_QUANTITY = true;

  const COPY = { quantity: "Qty: {count}" };

  const STATIC_CLASS = "boomba-qu-qty";
  const LINE_KEY_FLAG = "boombaQuKey";

  // ---------------------------------------------------------------------------
  // Debug - localStorage.setItem("boomba_qu_debug", "1")
  // ---------------------------------------------------------------------------

  const isDebugEnabled = (function () {
    try {
      return localStorage.getItem("boomba_qu_debug") === "1";
    } catch (error) {
      return false;
    }
  })();

  function log() {
    if (!isDebugEnabled) {
      return;
    }

    console.log.apply(console, ["[boomba-qu-lock]"].concat([].slice.call(arguments)));
  }

  // ---------------------------------------------------------------------------
  // Detection
  // ---------------------------------------------------------------------------

  /**
   * Recharge writes the plan's unit count into a selling plan option, and /cart.js carries
   * it through on the line. A standard subscription plan reports "0", a Quantity Upsell
   * plan reports the real count. So nothing here is hardcoded to a product.
   */
  function readPlanQuantity(item) {
    const allocation = item.selling_plan_allocation;
    const plan = allocation && allocation.selling_plan;

    if (!plan || !plan.options) {
      return 0;
    }

    const option = plan.options.find(function (candidate) {
      return /quantity/i.test(candidate.name || "");
    });

    if (!option) {
      return 0;
    }

    return parseInt(option.value, 10) || 0;
  }

  // ---------------------------------------------------------------------------
  // DOM
  // ---------------------------------------------------------------------------

  function buildStaticQuantity(count) {
    const label = document.createElement("span");

    label.className = STATIC_CLASS;
    label.textContent = COPY.quantity.replace("{count}", count);

    return label;
  }

  /**
   * Deliberately NOT short-circuited on an unchanged line.
   *
   * ampHideGiftQuantityButtons resets display:flex on every non-zero-priced line on every
   * cart update, so the hide has to be re-asserted each run even when the cart data is
   * identical. Every step below is idempotent, so a repeat run changes no DOM and
   * therefore triggers no further mutation.
   *
   * The line key is still stamped, because AMP recycles line elements: a node showing
   * product A gets repainted to show product B and keeps its children. The stamp is what
   * tells us a leftover label belongs to a different product.
   */
  function applyLock(lineElement, item) {
    const planQuantity = readPlanQuantity(item);
    const lineKey = [item.variant_id, item.quantity, planQuantity].join("|");
    const isRecycledLine = lineElement.dataset[LINE_KEY_FLAG] !== lineKey;

    lineElement.dataset[LINE_KEY_FLAG] = lineKey;

    const stepper = lineElement.querySelector(SELECTORS.quantitySelector);
    let label = lineElement.querySelector("." + STATIC_CLASS);

    if (!planQuantity) {
      // Not a Quantity Upsell line. Drop any label left over from a recycled node and
      // leave the stepper exactly as amp-upsell.js left it.
      if (label) {
        label.remove();
      }

      return;
    }

    if (!stepper) {
      return;
    }

    stepper.style.display = "none";

    if (!SHOW_STATIC_QUANTITY) {
      return;
    }

    const text = COPY.quantity.replace("{count}", item.quantity);

    if (label && (isRecycledLine || label.textContent !== text)) {
      label.remove();
      label = null;
    }

    if (!label) {
      const bottom = stepper.closest(SELECTORS.detailsBottom) || lineElement;

      bottom.insertBefore(buildStaticQuantity(item.quantity), stepper);
      log("locked", item.handle, "qty", item.quantity, "plan qty", planQuantity);
    }
  }

  /**
   * AMP renders no identifying attributes on a cart line, but DOM order matches
   * cart.items order, so the Nth line node is the Nth item.
   */
  function applyAllLocks(cart) {
    if (!cart || !cart.items) {
      return;
    }

    const lineElements = document.querySelectorAll(SELECTORS.cartLine);

    if (lineElements.length !== cart.items.length) {
      log("line count mismatch, skipping", lineElements.length, cart.items.length);
      return;
    }

    cart.items.forEach(function (item, index) {
      applyLock(lineElements[index], item);
    });
  }

  // ---------------------------------------------------------------------------
  // The theme's own cart: /cart and the native drawer
  // ---------------------------------------------------------------------------

  /**
   * AMP is not the only place a quantity can be changed. The theme renders its own cart
   * at /cart with a working stepper, so the same lock has to apply there.
   *
   * These rows carry data-index, which is the 1-based cart line number, so the row maps
   * to a cart item directly rather than by DOM order.
   */
  function applyThemeCartLocks(cart) {
    document.querySelectorAll(SELECTORS.themeQuantityInput).forEach(function (input) {
      const lineNumber = parseInt(input.dataset.index, 10);
      const item = cart.items[lineNumber - 1];

      if (!item) {
        return;
      }

      const wrap = input.closest(SELECTORS.themeQuantityWrap) || input.parentElement;

      if (!wrap) {
        return;
      }

      const planQuantity = readPlanQuantity(item);
      const container = wrap.parentElement || wrap;
      let label = container.querySelector("." + STATIC_CLASS);

      if (!planQuantity) {
        if (label) {
          label.remove();
          wrap.style.display = "";
        }

        return;
      }

      wrap.style.display = "none";

      if (!SHOW_STATIC_QUANTITY) {
        return;
      }

      const text = COPY.quantity.replace("{count}", item.quantity);

      if (label && label.textContent !== text) {
        label.remove();
        label = null;
      }

      if (!label) {
        container.insertBefore(buildStaticQuantity(item.quantity), wrap);
        log("locked theme cart line", lineNumber, item.handle);
      }
    });
  }

  // The theme cart is server rendered, so unlike AMP nobody hands us the cart. Fetch it,
  // debounced, and only when one of those rows is actually on the page.
  let themeRefreshPending = null;

  function refreshThemeCart() {
    if (themeRefreshPending || !document.querySelector(SELECTORS.themeQuantityInput)) {
      return;
    }

    themeRefreshPending = setTimeout(function () {
      themeRefreshPending = null;

      fetch(window.Shopify.routes.root + "cart.js")
        .then(function (response) {
          return response.json();
        })
        .then(applyThemeCartLocks)
        .catch(function (error) {
          log("theme cart fetch failed", error);
        });
    }, 150);
  }

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------

  let latestCart = null;

  /**
   * ampHideGiftQuantityButtons actively sets display:flex back on every non-zero-priced
   * line, so the lock has to be applied AFTER it on every update, not just once.
   */
  window.AMP_CART_UPDATED = function (cart, cartTarget) {
    latestCart = cart;

    if (typeof window.ampSyncCartCounts === "function") {
      window.ampSyncCartCounts(cart.item_count);
    }

    if (typeof window.ampHideGiftQuantityButtons === "function") {
      window.ampHideGiftQuantityButtons();
    }

    if (typeof window.ampCheckFreeGift === "function") {
      window.ampCheckFreeGift(cart, cartTarget);
    }

    applyAllLocks(cart);
    applyThemeCartLocks(cart);
  };

  window.AMP_CART_LOADED = function (cart) {
    latestCart = cart;
    applyAllLocks(cart);
    applyThemeCartLocks(cart);
  };

  /**
   * The theme re-renders its cart section after every quantity change, which wipes the
   * lock, so watch for that and re-apply. Cheap: refreshThemeCart bails immediately when
   * the page has no theme cart rows, which is every page except /cart and the drawer.
   */
  function startThemeCartObserver() {
    refreshThemeCart();

    new MutationObserver(refreshThemeCart).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function startObserver() {
    startThemeCartObserver();

    const root = document.querySelector(SELECTORS.ampRoot);

    if (!root) {
      return;
    }

    let pendingRun = null;

    // Debounced because the announcement carousel mutates this subtree too. The per-line
    // key makes a repeat run a no-op.
    const observer = new MutationObserver(function () {
      if (!latestCart || pendingRun) {
        return;
      }

      pendingRun = setTimeout(function () {
        pendingRun = null;
        applyAllLocks(latestCart);
      }, 150);
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startObserver);
  } else {
    startObserver();
  }

  window.BOOMBA_QU_LOCK = {
    version: "1.1.0",
    config: { SHOW_STATIC_QUANTITY: SHOW_STATIC_QUANTITY, COPY: COPY },
    getCart: function () {
      return latestCart;
    },
    apply: function () {
      applyAllLocks(latestCart);
    },
    readPlanQuantity: readPlanQuantity,
  };
})();
